import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  MagnifyingGlass,
  Plus,
  CircleNotch,
  X,
  Buildings,
} from '@phosphor-icons/react';
import { RowActions } from '@/components/shared/RowActions';
import { platformApi, getPlatformApiErrorMessage } from '@/lib/platformApi';
import { toast } from 'sonner';
import { ScrollLock } from '@/components/shared/ScrollLock';

// ── Types ────────────────────────────────────────────────────────────────────

interface Tenant {
  id: string;
  name: string;
  slug: string;
  legalName?: string;
  email?: string;
  status: string;
  plan: string;
  jurisdiction?: string;
  trialEndsAt?: string;
  createdAt: string;
  userCount: number;
}

interface TenantsResponse {
  data: Tenant[];
  pagination: { page: number; limit: number; total: number; totalPages: number };
}

interface CreateTenantPayload {
  name: string;
  slug: string;
  legalName?: string;
  email?: string;
  plan: string;
  trialDays?: number;
  firstAdmin: {
    email: string;
    firstName: string;
    lastName: string;
  };
}

interface CreatedTenantResult {
  firstAdmin: { email: string; temporaryPassword: string };
}

// ── Helpers ─────────────────────────────────────────────────────────────────

function formatDate(str: string) {
  return new Date(str).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function slugify(name: string) {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .slice(0, 40);
}

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, string> = {
    active: 'bg-plat-success-soft text-plat-success-text',
    trial: 'bg-plat-warning-soft text-plat-warning-text',
    suspended: 'bg-plat-danger-soft text-plat-danger-text',
  };
  return (
    <span className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium capitalize ${map[status] ?? 'bg-plat-neutral-soft text-plat-secondary'}`}>
      {status}
    </span>
  );
}

function PlanBadge({ plan }: { plan: string }) {
  const map: Record<string, string> = {
    trial: 'bg-plat-warning-soft text-plat-warning-text',
    starter: 'bg-plat-info-soft text-plat-info-text',
    pro: 'bg-plat-indigo-soft text-plat-indigo-text',
    enterprise: 'bg-plat-purple-soft text-plat-purple-text',
  };
  return (
    <span className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium capitalize ${map[plan] ?? 'bg-plat-neutral-soft text-plat-secondary'}`}>
      {plan}
    </span>
  );
}

// ── CreateTenantModal ────────────────────────────────────────────────────────

interface CreateTenantModalProps {
  onClose: () => void;
  onSuccess: () => void;
}

function CreateTenantModal({ onClose, onSuccess }: CreateTenantModalProps) {
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [legalName, setLegalName] = useState('');
  const [email, setEmail] = useState('');
  const [plan, setPlan] = useState('trial');
  const [trialDays, setTrialDays] = useState(14);
  const [adminEmail, setAdminEmail] = useState('');
  const [adminFirst, setAdminFirst] = useState('');
  const [adminLast, setAdminLast] = useState('');
  const [slugManual, setSlugManual] = useState(false);

  // Show credentials after success
  const [credentials, setCredentials] = useState<{ email: string; temporaryPassword: string } | null>(null);

  const mutation = useMutation({
    mutationFn: async (payload: CreateTenantPayload) => {
      const res = await platformApi.post('/tenants', payload);
      return (res.data?.data ?? res.data) as CreatedTenantResult;
    },
    onSuccess: (result) => {
      setCredentials(result.firstAdmin);
      onSuccess();
    },
    onError: (err) => {
      toast.error(getPlatformApiErrorMessage(err, 'Failed to create organisation'));
    },
  });

  // Auto-generate slug from name unless user has typed it manually
  useEffect(() => {
    if (!slugManual) setSlug(slugify(name));
  }, [name, slugManual]);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (mutation.isPending) return;
    mutation.mutate({
      name,
      slug,
      legalName: legalName || undefined,
      email: email || undefined,
      plan,
      trialDays: plan === 'trial' ? trialDays : undefined,
      firstAdmin: {
        email: adminEmail,
        firstName: adminFirst,
        lastName: adminLast,
      },
    });
  }

  // ── Credentials success screen
  if (credentials) {
    return (
      <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-plat-scrim/70 px-4">
        <ScrollLock />
        <div className="my-auto w-full max-w-md rounded-xl border border-plat-border bg-plat-surface p-6 shadow-2xl">
          <div className="mb-4 flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-plat-success-soft">
              <Buildings className="h-5 w-5 text-plat-success-text" />
            </div>
            <div>
              <div className="font-semibold text-plat-fg">Organisation created</div>
              <div className="text-xs text-plat-muted">Save the credentials shown below — they will not be shown again.</div>
            </div>
          </div>

          <div className="rounded-lg border border-plat-warning-line bg-plat-warning-soft p-4 mb-4">
            <div className="mb-1 text-xs font-bold uppercase tracking-wider text-plat-warning-text">First Admin Credentials</div>
            <div className="space-y-2 text-sm">
              <div>
                <span className="text-plat-muted">Email: </span>
                <span className="text-plat-fg">{credentials.email}</span>
              </div>
              <div>
                <span className="text-plat-muted">Temporary password: </span>
                <span className="text-plat-warning-text">{credentials.temporaryPassword}</span>
              </div>
            </div>
          </div>

          <p className="mb-4 text-xs text-plat-muted">
            The admin must change this password on first login. Share these credentials securely.
          </p>

          <button
            onClick={onClose}
            className="w-full rounded-lg bg-plat-accent-solid px-4 py-2.5 text-sm font-medium text-plat-on-accent hover:bg-plat-accent-solid-hover"
          >
            Done
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-plat-scrim/70 px-4">
      <ScrollLock />
      <div className="my-auto w-full max-w-lg rounded-xl border border-plat-border bg-plat-surface shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-plat-border px-6 py-4">
          <h2 className="text-[15px] font-semibold text-plat-fg">New Organisation</h2>
          <button onClick={onClose} className="text-plat-muted hover:text-plat-secondary">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="overflow-y-auto px-6 py-5 max-h-[80vh]">
          <div className="space-y-4">
            {/* Organisation details */}
            <div className="text-xs font-bold uppercase tracking-wider text-plat-muted mb-2">Organisation</div>

            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="mb-1.5 block text-xs font-medium text-plat-secondary">Name *</label>
                <input
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="h-9 w-full rounded-lg border border-plat-border bg-plat-bg px-3 text-sm text-plat-fg placeholder-plat-faint focus:border-plat-focus focus:outline-none focus:ring-2 focus:ring-plat-focus/50"
                  placeholder="Smith & Co Chambers"
                />
              </div>

              <div className="col-span-2">
                <label className="mb-1.5 block text-xs font-medium text-plat-secondary">Slug *</label>
                <input
                  required
                  value={slug}
                  onChange={(e) => { setSlug(e.target.value); setSlugManual(true); }}
                  className="h-9 w-full rounded-lg border border-plat-border bg-plat-bg px-3 text-sm text-plat-fg placeholder-plat-faint focus:border-plat-focus focus:outline-none focus:ring-2 focus:ring-plat-focus/50"
                  placeholder="smith-co-chambers"
                />
                <div className="mt-1 text-xs text-plat-faint">Auto-generated from name. Used in URLs.</div>
              </div>

              <div>
                <label className="mb-1.5 block text-xs font-medium text-plat-secondary">Legal name</label>
                <input
                  value={legalName}
                  onChange={(e) => setLegalName(e.target.value)}
                  className="h-9 w-full rounded-lg border border-plat-border bg-plat-bg px-3 text-sm text-plat-fg placeholder-plat-faint focus:border-plat-focus focus:outline-none focus:ring-2 focus:ring-plat-focus/50"
                  placeholder="Optional"
                />
              </div>

              <div>
                <label className="mb-1.5 block text-xs font-medium text-plat-secondary">Contact email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-9 w-full rounded-lg border border-plat-border bg-plat-bg px-3 text-sm text-plat-fg placeholder-plat-faint focus:border-plat-focus focus:outline-none focus:ring-2 focus:ring-plat-focus/50"
                  placeholder="clerk@chambers.co.uk"
                />
              </div>
            </div>

            {/* Plan */}
            <div>
              <label className="mb-1.5 block text-xs font-medium text-plat-secondary">Plan *</label>
              <select
                value={plan}
                onChange={(e) => setPlan(e.target.value)}
                className="h-9 w-full rounded-lg border border-plat-border bg-plat-bg px-3 text-sm text-plat-fg focus:border-plat-focus focus:outline-none focus:ring-2 focus:ring-plat-focus/50"
              >
                <option value="trial">Trial</option>
                <option value="starter">Starter</option>
                <option value="pro">Pro</option>
                <option value="enterprise">Enterprise</option>
              </select>
            </div>

            {plan === 'trial' && (
              <div>
                <label className="mb-1.5 block text-xs font-medium text-plat-secondary">Trial days</label>
                <input
                  type="number"
                  min={1}
                  max={365}
                  value={trialDays}
                  onChange={(e) => setTrialDays(Number(e.target.value))}
                  className="h-9 w-32 rounded-lg border border-plat-border bg-plat-bg px-3 text-sm text-plat-fg focus:border-plat-focus focus:outline-none focus:ring-2 focus:ring-plat-focus/50"
                />
              </div>
            )}

            {/* First admin */}
            <div className="border-t border-plat-border pt-4">
              <div className="text-xs font-bold uppercase tracking-wider text-plat-muted mb-3">First Admin Account</div>
              <div className="space-y-3">
                <div>
                  <label className="mb-1.5 block text-xs font-medium text-plat-secondary">Admin email *</label>
                  <input
                    type="email"
                    required
                    value={adminEmail}
                    onChange={(e) => setAdminEmail(e.target.value)}
                    className="h-9 w-full rounded-lg border border-plat-border bg-plat-bg px-3 text-sm text-plat-fg placeholder-plat-faint focus:border-plat-focus focus:outline-none focus:ring-2 focus:ring-plat-focus/50"
                    placeholder="admin@chambers.co.uk"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="mb-1.5 block text-xs font-medium text-plat-secondary">First name *</label>
                    <input
                      required
                      value={adminFirst}
                      onChange={(e) => setAdminFirst(e.target.value)}
                      className="h-9 w-full rounded-lg border border-plat-border bg-plat-bg px-3 text-sm text-plat-fg placeholder-plat-faint focus:border-plat-focus focus:outline-none focus:ring-2 focus:ring-plat-focus/50"
                    />
                  </div>
                  <div>
                    <label className="mb-1.5 block text-xs font-medium text-plat-secondary">Last name *</label>
                    <input
                      required
                      value={adminLast}
                      onChange={(e) => setAdminLast(e.target.value)}
                      className="h-9 w-full rounded-lg border border-plat-border bg-plat-bg px-3 text-sm text-plat-fg placeholder-plat-faint focus:border-plat-focus focus:outline-none focus:ring-2 focus:ring-plat-focus/50"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="mt-5 flex items-center justify-end gap-3 border-t border-plat-border pt-4">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-plat-border px-4 py-2 text-sm font-medium text-plat-secondary hover:border-plat-border-strong hover:text-plat-fg"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={mutation.isPending}
              className="inline-flex items-center gap-2 rounded-lg bg-plat-accent-solid px-4 py-2 text-sm font-medium text-plat-on-accent hover:bg-plat-accent-solid-hover disabled:cursor-not-allowed disabled:bg-plat-neutral-soft disabled:text-plat-disabled"
            >
              {mutation.isPending ? <CircleNotch className="h-4 w-4 animate-spin" /> : null}
              Create Organisation
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── TenantsPage ──────────────────────────────────────────────────────────────

export default function TenantsPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [showCreate, setShowCreate] = useState(false);

  // Debounce search input
  const debounce = useCallback(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    const cleanup = debounce();
    return cleanup;
  }, [debounce]);

  // Reset page when filters change
  useEffect(() => { setPage(1); }, [debouncedSearch, statusFilter]);

  const { data, isLoading, error } = useQuery<TenantsResponse>({
    queryKey: ['platform', 'tenants', debouncedSearch, statusFilter, page],
    queryFn: async () => {
      const res = await platformApi.get('/tenants', {
        params: { search: debouncedSearch || undefined, status: statusFilter || undefined, page, limit: 25 },
      });
      // listTenants returns { data, pagination } and the controller sends it unwrapped.
      return res.data;
    },
  });

  function handleCreateSuccess() {
    queryClient.invalidateQueries({ queryKey: ['platform', 'tenants'] });
  }

  const suspendMutation = useMutation({
    mutationFn: async (id: string) => {
      await platformApi.post(`/tenants/${id}/suspend`, {});
    },
    onSuccess: () => {
      toast.success('Organisation suspended');
      queryClient.invalidateQueries({ queryKey: ['platform', 'tenants'] });
    },
    onError: (err) => {
      toast.error(getPlatformApiErrorMessage(err, 'Failed to suspend organisation'));
    },
  });

  const activateMutation = useMutation({
    mutationFn: async (id: string) => {
      await platformApi.post(`/tenants/${id}/activate`, {});
    },
    onSuccess: () => {
      toast.success('Organisation reactivated');
      queryClient.invalidateQueries({ queryKey: ['platform', 'tenants'] });
    },
    onError: (err) => {
      toast.error(getPlatformApiErrorMessage(err, 'Failed to reactivate organisation'));
    },
  });

  return (
    <div className="space-y-5">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <MagnifyingGlass className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-plat-muted" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search organisations…"
            className="h-9 w-full rounded-lg border border-plat-border bg-plat-surface pl-9 pr-3 text-sm text-plat-fg placeholder-plat-faint focus:border-plat-focus focus:outline-none focus:ring-2 focus:ring-plat-focus/50"
          />
        </div>

        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="h-9 rounded-lg border border-plat-border bg-plat-surface px-3 text-sm text-plat-fg focus:border-plat-focus focus:outline-none focus:ring-2 focus:ring-plat-focus/50"
        >
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="trial">Trial</option>
          <option value="suspended">Suspended</option>
        </select>

        <button
          onClick={() => setShowCreate(true)}
          className="ml-auto inline-flex items-center gap-2 rounded-lg bg-plat-accent-solid px-4 py-2 text-sm font-medium text-plat-on-accent hover:bg-plat-accent-solid-hover"
        >
          <Plus className="h-4 w-4" />
          New Organisation
        </button>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <CircleNotch className="h-7 w-7 animate-spin text-plat-info-text" />
        </div>
      ) : error ? (
        <div className="rounded-xl border border-plat-danger-line bg-plat-danger-soft p-6 text-sm text-plat-danger-text">
          {getPlatformApiErrorMessage(error)}
        </div>
      ) : (
        <>
          <div className="overflow-hidden rounded-xl border border-plat-border bg-plat-surface">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-plat-border">
                  <th className="px-5 py-3.5 text-xs font-semibold uppercase tracking-[0.06em] text-plat-muted">Organisation</th>
                  <th className="px-5 py-3.5 text-xs font-semibold uppercase tracking-[0.06em] text-plat-muted">Plan</th>
                  <th className="px-5 py-3.5 text-xs font-semibold uppercase tracking-[0.06em] text-plat-muted">Status</th>
                  <th className="px-5 py-3.5 text-xs font-semibold uppercase tracking-[0.06em] text-plat-muted">Users</th>
                  <th className="px-5 py-3.5 text-xs font-semibold uppercase tracking-[0.06em] text-plat-muted">Trial ends</th>
                  <th className="px-5 py-3.5 text-xs font-semibold uppercase tracking-[0.06em] text-plat-muted">Created</th>
                  <th className="px-5 py-3.5 text-xs font-semibold uppercase tracking-[0.06em] text-plat-muted"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-plat-border">
                {(data?.data ?? []).map((t) => (
                  <tr key={t.id} className="transition hover:bg-plat-hover">
                    <td className="px-5 py-3.5">
                      <button
                        onClick={() => navigate(`/platform/tenants/${t.id}`)}
                        className="text-left font-medium text-plat-fg hover:text-plat-info-text hover:underline"
                      >
                        {t.name}
                      </button>
                      <div className="text-xs text-plat-muted">{t.slug}</div>
                    </td>
                    <td className="px-5 py-3.5"><PlanBadge plan={t.plan} /></td>
                    <td className="px-5 py-3.5"><StatusBadge status={t.status} /></td>
                    <td className="px-5 py-3.5 text-plat-secondary">{t.userCount}</td>
                    <td className="px-5 py-3.5 text-plat-secondary">
                      {t.trialEndsAt ? formatDate(t.trialEndsAt) : <span className="text-plat-faint">—</span>}
                    </td>
                    <td className="px-5 py-3.5 text-plat-secondary">{formatDate(t.createdAt)}</td>
                    <td className="px-5 py-3.5 text-right">
                      {t.status === 'suspended' ? (
                        <RowActions
                          className="justify-end"
                          onView={() => navigate(`/platform/tenants/${t.id}`)}
                          onDelete={() => activateMutation.mutate(t.id)}
                          deleteLabel="Reactivate"
                          deleteConfirm={{
                            title: 'Reactivate organisation?',
                            message: `Reactivate “${t.name}”? Its users will regain access immediately.`,
                            confirmLabel: 'Reactivate',
                          }}
                        />
                      ) : (
                        <RowActions
                          className="justify-end"
                          onView={() => navigate(`/platform/tenants/${t.id}`)}
                          onDelete={() => suspendMutation.mutate(t.id)}
                          deleteLabel="Suspend"
                          deleteConfirm={{
                            title: 'Suspend organisation?',
                            message: `Suspend “${t.name}”? All its users will lose access until it is reactivated.`,
                            confirmLabel: 'Suspend',
                          }}
                        />
                      )}
                    </td>
                  </tr>
                ))}
                {(data?.data ?? []).length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-5 py-10 text-center text-sm text-plat-muted">
                      No organisations found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {data && data.pagination.totalPages > 1 && (
            <div className="flex items-center justify-between text-sm">
              <span className="text-plat-muted">
                Page {data.pagination.page} of {data.pagination.totalPages} — {data.pagination.total} total
              </span>
              <div className="flex gap-2">
                <button
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className="rounded-lg border border-plat-border px-3 py-1.5 text-xs text-plat-secondary hover:border-plat-border-strong hover:text-plat-fg disabled:cursor-not-allowed disabled:text-plat-disabled"
                >
                  Previous
                </button>
                <button
                  disabled={page >= data.pagination.totalPages}
                  onClick={() => setPage((p) => p + 1)}
                  className="rounded-lg border border-plat-border px-3 py-1.5 text-xs text-plat-secondary hover:border-plat-border-strong hover:text-plat-fg disabled:cursor-not-allowed disabled:text-plat-disabled"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Create modal */}
      {showCreate && (
        <CreateTenantModal
          onClose={() => setShowCreate(false)}
          onSuccess={handleCreateSuccess}
        />
      )}
    </div>
  );
}
